import { CustomAmountMode, OrderResponse } from '../schema'
import { NumberUtils } from './NumberUtils'
import { DEFAULT_DECIMALS } from '../currency'

type PaymentDiscountInfo = {
  discount: number
  input: {
    orders: {
      id: number
    }[]
  }
}

export type PayListItem = {
  id: number
  amount: number
}

const getOrderAmount = (
  orderId: number,
  orders: Pick<OrderResponse, 'id' | 'amount' | 'final_price'>[],
  discounts?: PaymentDiscountInfo[]
) => {
  const order = orders?.find((o) => o.id === orderId)
  if (order?.final_price !== undefined) {
    return order.final_price
  }
  const activeDiscounts = discounts?.filter(
    (d) => d.input.orders[0].id === orderId
  )
  const amount = order?.amount ?? 0
  if (!activeDiscounts?.length) {
    return amount
  }
  let finalAmount = amount
  activeDiscounts.forEach((d) => {
    finalAmount += Math.min(0, d.discount)
  })
  return finalAmount
}

const getAdjustedAmount = (
  order: number,
  toPay: number,
  total: number,
  decimals = DEFAULT_DECIMALS
) => {
  if (toPay >= total) {
    return order
  }
  const fraction = toPay / total

  return NumberUtils.ceil.decimal(order * fraction, decimals)
}

const getPayList = (
  customAmount: number,
  orders: Pick<OrderResponse, 'id' | 'amount'>[],
  discounts?: PaymentDiscountInfo[],
  decimals = DEFAULT_DECIMALS
) => {
  const total =
    orders?.reduce(
      (acc, order) =>
        acc + PaymentUtils.getOrderAmount(order.id, orders, discounts),
      0
    ) ?? 0

  const minPayment = NumberUtils.ceil.decimal(total / 2, decimals)

  const paymentAmount = customAmount >= minPayment ? customAmount : minPayment

  const payList = orders.map((order) => ({
    id: order.id,
    amount: getAdjustedAmount(
      getOrderAmount(order.id, orders, discounts),
      paymentAmount,
      total,
      decimals
    ),
  }))

  return payList satisfies PayListItem[]
}

const getPaymentTotal = (
  orders: Pick<OrderResponse, 'id' | 'amount' | 'final_price'>[],
  discounts?: PaymentDiscountInfo[],
  decimals = DEFAULT_DECIMALS
) => {
  const total =
    orders?.reduce(
      (acc, order) => acc + getOrderAmount(order.id, orders, discounts),
      0
    ) ?? 0
  return NumberUtils.round.decimal(total, decimals)
}

const getMinMax = ({
  toPay,
  fee,
  custom = 'disabled',
  decimals = DEFAULT_DECIMALS,
}: {
  toPay: number
  fee?: number
  custom?: CustomAmountMode
  decimals?: number
}) => {
  const max = toPay + (fee ?? 0)
  const min =
    custom === 'enabled' ? NumberUtils.ceil.decimal(toPay / 2, decimals) : max

  return { min, max }
}

export const PaymentUtils = {
  getOrderAmount,
  getAdjustedAmount,
  getPayList,
  getPaymentTotal,
  getMinMax,
}
